import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from './legal/LegalPage';
import { LEGAL } from './legal/legalConfig';

// Keep in step with what the app and backend actually do: who receives data
// (Supabase, OpenRouter, Stripe, Resend, DataFast, Google Fonts), what the
// tracked links record and what the browser stores.
const mail = <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>;

const summary = (
    <ul>
        <li>We collect what you give us (your account, your product details and what you create) and only use it to run {LEGAL.product}.</li>
        <li>Your content is sent to AI model providers to generate posts. We don't use it to train AI models, and we don't sell personal information or show ads.</li>
        <li>Payments go through Stripe. We never see or store your full card number.</li>
        <li>Tracked links count clicks without storing visitors' IP addresses.</li>
        <li>You can delete your account and its data yourself at any time, or ask us for a copy of your data: {mail}.</li>
    </ul>
);

const sections = [
    {
        id: 'who-we-are',
        title: 'Who we are',
        body: (
            <>
                <p>
                    {LEGAL.product} ("we", "us") is operated by {LEGAL.company}, and this policy explains what
                    personal information we collect when you use {LEGAL.site} and the {LEGAL.product} app (the "Service"), how we
                    use it, who we share it with and the choices you have. For data about you, we are the controller.
                </p>
                <p>Questions or requests about privacy: {mail}.</p>
            </>
        ),
    },
    {
        id: 'what-we-collect',
        title: 'What we collect',
        body: (
            <>
                <h3>Information you give us</h3>
                <ul>
                    <li><strong>Account details:</strong> your email address and, if you sign in with Google, your name and profile picture from Google. If you use a password, it's handled by our authentication provider and we never see it.</li>
                    <li><strong>Product information:</strong> what you tell us about the products you market: website and app store links, files you upload (such as PDFs and images), descriptions, pricing, features, customer profiles (ICPs), brand voice, channels and quiz answers.</li>
                    <li><strong>What you create:</strong> prompts, weekly content plans and their targets, posts, articles, ad copy, scripts, images, videos, notes in the Memory tab and the results you enter (such as views and likes).</li>
                    <li><strong>Billing details:</strong> your plan, billing country and, where tax applies, your billing address and tax ID. Card and wallet details (Apple Pay, Google Pay) go directly to Stripe.</li>
                    <li><strong>Messages:</strong> anything you send us by email, including cancellation feedback.</li>
                </ul>

                <h3>Information collected automatically</h3>
                <ul>
                    <li><strong>Usage:</strong> how many generations and videos you use each month, so we can apply plan limits.</li>
                    <li><strong>Website analytics:</strong> pages visited, referring site, approximate location (country), browser and device type, collected by our analytics provider when you visit {LEGAL.site}.</li>
                    <li><strong>Technical data:</strong> IP address, browser and device details in server logs, used to keep the Service secure and working.</li>
                </ul>

                <h3>Information from public sources</h3>
                <p>
                    When you add a website or app store listing, our servers read that public page (and, for app
                    listings, recent public reviews) to fill in your product profile.
                </p>

                <h3>People who click your tracked links</h3>
                <p>
                    When someone clicks a short link you created, we record the time, the link, the referring website,
                    whether they're on mobile or desktop, and a one-way hash of their IP address and browser that
                    changes every day, used only to count unique visitors. <strong>We don't store their IP address</strong>,
                    and clicks from link-preview bots and crawlers aren't counted. You're responsible for telling your
                    own audience about this where the law requires it.
                </p>
            </>
        ),
    },
    {
        id: 'how-we-use',
        title: 'How we use it',
        body: (
            <>
                <ul>
                    <li><strong>To provide the Service</strong> (performance of our contract with you): building product profiles and ICPs, generating and planning content, tracking links, keeping your memory files and applying your plan's limits.</li>
                    <li><strong>To bill you</strong> (contract and legal obligation): processing payments and trials, calculating tax, sending invoices and receipts, and keeping records tax law requires.</li>
                    <li><strong>To email you about your account</strong> (contract): for example, that a free trial ends in 3 days or that a payment failed. These aren't marketing emails.</li>
                    <li><strong>To improve and secure the Service</strong> (legitimate interests): understanding which features and pages are used, preventing abuse and fixing problems.</li>
                    <li><strong>To meet legal obligations</strong> and respond to lawful requests.</li>
                </ul>
                <p>
                    If we ever want to send you marketing emails we'll ask first, and every such email will have an
                    unsubscribe link. We don't make decisions about you that have legal or similarly significant
                    effects using automated processing alone.
                </p>
            </>
        ),
    },
    {
        id: 'ai',
        title: 'AI processing',
        body: (
            <>
                <p>
                    To generate content, we send the relevant parts of your product information, customer profiles,
                    brand voice, memory files and prompts to AI models through OpenRouter, which routes each request to
                    the model provider that runs it. Those providers process the content to return a result, under
                    their own terms and privacy policies.
                </p>
                <p>
                    We don't use your content to train AI models, and we don't sell it. Don't include information in
                    your prompts or uploads that you don't want processed this way, such as other people's sensitive
                    personal information.
                </p>
            </>
        ),
    },
    {
        id: 'sharing',
        title: 'Who we share it with',
        body: (
            <>
                <p>
                    We don't sell or rent personal information, and we don't share it for advertising. We share it only
                    with service providers that help us run {LEGAL.product}, under contracts that limit how they can use
                    it:
                </p>
                <table className="lg-table">
                    <thead>
                        <tr><th>Provider</th><th>What for</th><th>What they receive</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>Supabase</td><td>Sign-in and database</td><td>Account details and the data you store in the Service</td></tr>
                        <tr><td>OpenRouter and the AI model providers it routes to</td><td>Generating content</td><td>The content of each generation request</td></tr>
                        <tr><td>Stripe</td><td>Payments, subscriptions, invoices and tax</td><td>Email, payment details, billing address and tax ID</td></tr>
                        <tr><td>Resend</td><td>Account emails</td><td>Email address and the email's content</td></tr>
                        <tr><td>DataFast</td><td>Website analytics</td><td>Visit data described above</td></tr>
                        <tr><td>Google (Fonts)</td><td>Serving the site's fonts</td><td>Your IP address and browser details, when your browser loads the fonts</td></tr>
                        <tr><td>Our cloud hosting providers</td><td>Running our servers</td><td>Data processed by our servers</td></tr>
                    </tbody>
                </table>
                <p>
                    We may also share information if the law requires it, to protect the rights, safety or property of
                    our users, us or others, or as part of a merger, acquisition or sale of assets, in which case this
                    policy will continue to apply to your information.
                </p>
            </>
        ),
    },
    {
        id: 'cookies',
        title: 'Cookies and browser storage',
        body: (
            <>
                <p>
                    The app stores a few things in your browser's local storage to work and to load faster: your
                    sign-in session, the product you last selected, the currency you picked, a copy of your plan and
                    pricing shown while fresh data loads, your whiteboard, plan preferences and report entries you log.
                    Cached plan details are cleared when you sign out. Our analytics provider may use a first-party
                    cookie or similar identifier to count visits. Stripe sets its own cookies on its checkout and billing
                    pages to process payments and prevent fraud.
                </p>
                <p>
                    You can clear local storage and cookies in your browser's settings at any time. The app needs local
                    storage for sign-in to work.
                </p>
            </>
        ),
    },
    {
        id: 'retention',
        title: 'How long we keep it',
        body: (
            <ul>
                <li><strong>Account and content:</strong> while your account is open. You can delete your account at any time under Plans &amp; billing → Delete account: your account data and content are deleted straight away, apart from what we must keep by law. If you email us instead, we'll do it within 30 days.</li>
                <li><strong>Products:</strong> deleting a product in the app also deletes its customer profiles, memory files and tracked links.</li>
                <li><strong>Billing records:</strong> invoices and payment records are kept for as long as tax and accounting laws require (typically up to 7 years), by us and by Stripe.</li>
                <li><strong>Tracked link clicks:</strong> while the link exists; deleting the link deletes its click history.</li>
                <li><strong>Server logs:</strong> for a limited period for security and debugging.</li>
            </ul>
        ),
    },
    {
        id: 'your-rights',
        title: 'Your rights and choices',
        body: (
            <>
                <p>Depending on where you live, you may have the right to:</p>
                <ul>
                    <li>access the personal information we hold about you and get a copy of it in a portable format;</li>
                    <li>correct information that's wrong;</li>
                    <li>delete your information;</li>
                    <li>object to, or ask us to restrict, how we use it;</li>
                    <li>withdraw consent where we rely on it;</li>
                    <li>complain to your local data protection authority.</li>
                </ul>
                <p>
                    You can update most of your information yourself in the app and delete your account under Plans &amp;
                    billing. For anything else, email {mail} from
                    the address on your account. We'll respond within 30 days and won't treat you differently for
                    exercising your rights.
                </p>
                <p>
                    <strong>California residents:</strong> we don't sell or share personal information for
                    cross-context behavioral advertising, and we don't use sensitive personal information to infer
                    characteristics about you. The categories of information we collect, their sources and purposes,
                    and who we disclose them to are described above.
                </p>
            </>
        ),
    },
    {
        id: 'transfers',
        title: 'International transfers',
        body: (
            <p>
                Our service providers process data in the United States and other countries. Where data about people in
                the European Economic Area, the United Kingdom or Switzerland is transferred to countries without an
                adequacy decision, we rely on appropriate safeguards such as the European Commission's Standard
                Contractual Clauses.
            </p>
        ),
    },
    {
        id: 'security',
        title: 'Security',
        body: (
            <p>
                We use encryption in transit, access controls and providers with strong security practices. Card
                details are handled by Stripe, which is certified to PCI DSS Level 1. No system is perfectly secure; if
                a breach affects your personal information, we'll notify you and the authorities as the law requires.
            </p>
        ),
    },
    {
        id: 'children',
        title: 'Children',
        body: (
            <p>
                The Service is for businesses and isn't directed to children. You must be at least 16 to use it. If you
                believe a child has given us personal information, contact us and we'll delete it.
            </p>
        ),
    },
    {
        id: 'changes',
        title: 'Changes to this policy',
        body: (
            <p>
                If we change this policy we'll update the date at the top. If a change is significant, we'll tell you
                by email or in the app before it takes effect. See also our <Link to="/terms">Terms of Service</Link>.
            </p>
        ),
    },
    {
        id: 'contact',
        title: 'Contact us',
        body: <p>{LEGAL.company}, {mail}.</p>,
    },
];

const PrivacyPage = () => (
    <LegalPage title="Privacy Policy" current="privacy" summary={summary} sections={sections} />
);

export default PrivacyPage;
